import React from "react";
import { useTranslation, Trans } from "react-i18next";

import "../practitioners/practitoner/practitoner.css";

import FavoriteToggle from "../favoritetoggle";

/**
 * One medium's info card on the Professional Sessions listing.
 *
 * Lifted verbatim out of practitioners/index.js so the favourites page can render
 * the identical card - the Book Now flow reads pre_pay_session_type /
 * post_pay_schedule / session_length straight off `item`, so the two screens must
 * not be allowed to drift apart.
 *
 * The only addition is the favourite toggle, which the caller owns: this
 * component renders `item.is_favorite` and reports taps, nothing more.
 */

const add3Dots = (string, limit) => {
  var dots = "...";
  if (string.length > limit) {
    // you can also use substr instead of substring
    string = string.substring(0, limit) + dots;
  }

  return string;
};

const checkSessionExtraPersion = (item, t) => {
  const obj = JSON.parse(item.session_length);

  const sessionKeys = Object.keys(obj);
  const isOffered = sessionKeys.includes("452") || sessionKeys.includes("602");
  if (isOffered) {
    return "";
  } else {
    return <em>{t("card.notOffered")}</em>;
  }
};

const checkSessionFoutyFiveExtraPersion = (item, t) => {
  if (Array.isArray(item.offerings)) {
    const offering = item.offerings.find((o) => o.key === "452");
    if (!offering) {
      return <em>{t("card.notOffered")}</em>;
    }
    let delta = offering.extra_person_delta;
    if (delta === null || delta === undefined || delta === "") {
      delta =
        offering.base_price !== null && offering.base_price !== undefined && offering.base_price !== ""
          ? parseFloat(offering.price) - parseFloat(offering.base_price)
          : offering.price;
    }
    return "$ " + delta + " " + offering.currency;
  }

  // Fallback for API responses without the `offerings` array yet
  const session_currency_obj = JSON.parse(item.session_currency);
  const obj = JSON.parse(item.session_length);
  const sessionCurrencyKeys = Object.keys(session_currency_obj);
  const sessionKeys = Object.keys(obj);
  const isOffered = sessionKeys.includes("452");
  const sessionCurrency = sessionCurrencyKeys.includes("452");
  let currency = "USD";
  if (sessionCurrency) {
    currency = session_currency_obj[452];
  }
  if (isOffered) {
    return "$ " + obj[452] + " " + currency;
  } else {
    return <em>{t("card.notOffered")}</em>;
  }
};

const checkSessionSixtyExtraPersion = (item, t) => {
  if (Array.isArray(item.offerings)) {
    const offering = item.offerings.find((o) => o.key === "602");
    if (!offering) {
      return <em>{t("card.notOffered")}</em>;
    }
    let delta = offering.extra_person_delta;
    if (delta === null || delta === undefined || delta === "") {
      delta =
        offering.base_price !== null && offering.base_price !== undefined && offering.base_price !== ""
          ? parseFloat(offering.price) - parseFloat(offering.base_price)
          : offering.price;
    }
    return "$ " + delta + " " + offering.currency;
  }

  // Fallback for API responses without the `offerings` array yet
  const session_currency_obj = JSON.parse(item.session_currency);
  const obj = JSON.parse(item.session_length);
  const sessionCurrencyKeys = Object.keys(session_currency_obj);
  const sessionKeys = Object.keys(obj);
  const isOffered = sessionKeys.includes("602");
  const sessionCurrency = sessionCurrencyKeys.includes("602");
  let currency = "USD";
  if (sessionCurrency) {
    currency = session_currency_obj[602];
  }
  if (isOffered) {
    return "$ " + obj[602] + " " + currency;
  } else {
    return <em>{t("card.notOffered")}</em>;
  }
};

const isfourtyFive = (item) => {
  if (item.session_length !== "" && item.session_length !== undefined) {
    const obj = JSON.parse(item.session_length);
    const sessionKeys = Object.keys(obj);
    const isOffered = sessionKeys.includes("452");
    if (!isOffered) {
      return true;
    }
  } else {
    return true;
  }
};

const isSixtyForTwo = (item) => {
  if (item.session_length !== "" && item.session_length !== undefined) {
    const obj = JSON.parse(item.session_length);
    const sessionKeys = Object.keys(obj);
    const isOffered = sessionKeys.includes("602");
    if (!isOffered) {
      return true;
    }
  } else {
    return true;
  }
};

export default function MediumCard({
  item,
  onBookNow,
  onViewProfile,
  onToggleFavorite = () => {},
  canFavorite = false,
  isTogglingFavorite = false,
  isOpeningProfile = false,
}) {
  const { t } = useTranslation("clientPremium");
  return (
    <div className="schedule-advance-bx">
      <div className="profile-box-top">
        {canFavorite && (
          <FavoriteToggle
            isFavorite={item.is_favorite}
            disabled={isTogglingFavorite}
            onToggle={() => onToggleFavorite(item)}
          />
        )}
        <div className="left-prof-bx">
          <div className="prof-circal image-advance-client-profile padding-unset profile-wrapper">
            <img
              className="practioner-profile-image"
              src={
                item.profile_image !== ""
                  ? process.env.REACT_APP_IMAGE_URL + item.profile_image
                  : "images/user-dummy.png"
              }
              alt="avatar"
              width="130"
              height="130"
              loading="lazy"
              decoding="async"
            />
            {(item.is_verify_token == 1 || item.is_verify_token == 2) && (
              <div className="hoverContent">
                <div className="hover-container">
                  <div className="hover-target" tabIndex={0}>
                    <div className="verified-badge">{t("verified.badge")}</div>
                  </div>
                  <aside className="hover-popup-r hover-popup-small ">
                    <h2 className="hoverHeading">
                      <span className="hoverBadge">{t("verified.badge")}</span>
                    </h2>
                    <p className="verified-para">
                      <Trans
                        i18nKey="verified.cardDescription"
                        ns="clientPremium"
                        components={{
                          a: (
                            <a
                              href="https://verified.verysoul.com/"
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          ),
                        }}
                      />
                    </p>
                  </aside>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="left-name-bx">
          <span>{item.name}</span>
        </div>
        <div className="button-select-profile d-flex align-items-center justify-content-center">
          {item.show_flexbooker ? (
            <button
              className="book-nw"
              onClick={() => window.open(item.personal_booking_link, "_self")}
            >
              {t("card.bookNow")}
            </button>
          ) : (
            item.session_length !== undefined &&
            item.session_length !== "[]" && (
              <button className="book-nw" onClick={() => onBookNow(item)}>
                {t("card.bookNow")}
              </button>
            )
          )}
          <button
            className="view-pro"
            disabled={isOpeningProfile}
            onClick={() => onViewProfile(item)}
          >
            {isOpeningProfile ? t("card.opening") : t("card.viewProfile")}
          </button>
        </div>
      </div>
      <div className="language-list-box">
        <ul>
          <li className="d-flex">
            <span className="min-width-70px">
              <img src="images/Language.png" alt="badge" />
              {t("card.languages")}
            </span>
            <div>
              <em>{add3Dots(item.languages, 100)}</em>
            </div>
          </li>
          <li className="d-flex">
            <span className="min-width-70px">
              <img src="images/wordtow.png" alt="badge" />
              {t("card.country")}
            </span>
            <div>
              <em>{item.country}</em>
            </div>
          </li>
          <li className="d-flex">
            <span className="min-width-70px">
              <img src="images/fluent_payment-48-filled.png" alt="badge" />
              {t("card.payment")}
            </span>
            <div>
              {item.payType.includes(1) ? <em>{t("card.payToBook")}</em> : ""}
              {item.payType.includes(2) ? <em>{t("card.payAfterSession")}</em> : ""}
            </div>
          </li>
          <li className="d-flex">
            <span className="min-width-70px">
              <img src="images/ph_video-camera-fill.png" alt="badge" />
              {t("card.recording")}
            </span>
            <div>
              {item.recording === 1 ? (
                <em>{t("card.allowed")}</em>
              ) : item.recording === 0 ? (
                <em>{t("card.notAvailable")}</em>
              ) : (
                ""
              )}
            </div>
          </li>
          <li className="d-flex margin-unset">
            <span className="min-width-70px">
              <img src="images/Badge_1_.png" alt="badge" />
              {t("card.offerings")}
            </span>
            <div>{item.session_length === "[]" && <em>{t("card.notOffered")}</em>}</div>
          </li>

          {item.session_length !== undefined &&
            item.session_length !== "[]" &&
            Object.entries(JSON.parse(item.session_length)).map(
              ([key, value]) =>
                key != 452 && key != 602 ? (
                  <li className="d-flex margin-unset" key={key}>
                    <span className="min-width-70px">
                      {t("card.minutes", { minutes: key })}
                    </span>
                    <div>
                      <em className="d-block">
                        {t("card.price", {
                          price: value,
                          currency:
                            Object.values(JSON.parse(item.session_currency))[0] ||
                            "",
                        })}
                      </em>
                    </div>
                  </li>
                ) : (
                  ""
                )
            )}

          <span className="prof-price-info app-prof"></span>

          <li className="d-flex margin-unset">
            <span className="min-width-70px">{t("card.extraPerson")}</span>
            <div>
              {item.session_length !== "" && checkSessionExtraPersion(item, t)}
            </div>
          </li>

          <>
            {item.session_length !== "" && isfourtyFive(item) ? (
              " "
            ) : (
              <li className="d-flex margin-unset">
                <span className="min-width-70px">{t("card.fortyFiveForTwo")}</span>
                <em>
                  {item.session_length !== "" &&
                    checkSessionFoutyFiveExtraPersion(item, t)}
                </em>
              </li>
            )}
          </>

          <>
            {item.session_length !== "" && isSixtyForTwo(item) ? (
              " "
            ) : (
              <li className="d-flex margin-unset">
                <span className="min-width-70px">{t("card.sixtyForTwo")}</span>
                <em>
                  {item.session_length !== "" &&
                    checkSessionSixtyExtraPersion(item, t)}
                </em>
              </li>
            )}
          </>
        </ul>
      </div>
    </div>
  );
}
